/**
 * Boli Live - Cart Store (Zustand)
 *
 * Global shopping cart state management.
 * Wraps @boli/core CartService for web-specific state.
 */

import { create } from 'zustand';
import { getCartService } from '@boli/core';
import type { Cart, GeoLocation, Order, CheckoutResponse } from '@boli/types';

interface CartState {
  // State
  cart: Cart | null;
  isLoading: boolean;
  isUpdating: boolean;
  itemCount: number;
  error: string | null;

  // Checkout State
  isCheckingOut: boolean;
  lastOrder: Order | null;
  checkoutError: string | null;

  // UI State
  isDrawerOpen: boolean;

  // Actions
  fetchCart: () => Promise<void>;
  addItem: (productId: string, quantity?: number) => Promise<boolean>;
  updateItemQuantity: (itemId: string, quantity: number) => Promise<boolean>;
  removeItem: (itemId: string) => Promise<boolean>;
  clearCart: () => Promise<boolean>;
  refreshCartCount: () => Promise<void>;

  // Checkout Actions
  checkout: (data: {
    shippingAddress: GeoLocation;
    paymentMethod: 'wallet' | 'cod' | 'jazzcash' | 'easypaisa';
    notes?: string;
  }) => Promise<CheckoutResponse>;
  clearCheckoutError: () => void;
  clearLastOrder: () => void;

  // UI Actions
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
}

const emptyCart: Cart = {
  items: [],
  subtotal: 0,
  shippingTotal: 0,
  pstTotal: 0,
  grandTotal: 0,
  itemCount: 0,
};

export const useCartStore = create<CartState>((set, get) => ({
  // Initial state
  cart: null,
  isLoading: false,
  isUpdating: false,
  itemCount: 0,
  error: null,

  // Checkout State
  isCheckingOut: false,
  lastOrder: null,
  checkoutError: null,

  // UI State
  isDrawerOpen: false,

  // Fetch full cart with products
  fetchCart: async () => {
    set({ isLoading: true, error: null });

    try {
      const cartService = getCartService();
      const cart = await cartService.getCart();

      set({
        cart,
        itemCount: cart.itemCount,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      set({
        cart: emptyCart,
        itemCount: 0,
        isLoading: false,
        error: 'Failed to load cart',
      });
    }
  },

  // Add item to cart
  addItem: async (productId: string, quantity: number = 1) => {
    set({ isUpdating: true, error: null });

    try {
      const cartService = getCartService();
      const result = await cartService.addItem({ productId, quantity });

      if (result.success) {
        // Refresh the full cart to get updated totals
        await get().fetchCart();
        set({ isUpdating: false });
        return true;
      } else {
        set({
          isUpdating: false,
          error: result.error?.message || 'Failed to add item',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      set({
        isUpdating: false,
        error: 'Failed to add item to cart',
      });
      return false;
    }
  },

  // Update item quantity
  updateItemQuantity: async (itemId: string, quantity: number) => {
    set({ isUpdating: true, error: null });

    try {
      const cartService = getCartService();

      if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        return get().removeItem(itemId);
      }

      const result = await cartService.updateItem(itemId, { quantity });

      if (result.success) {
        // Refresh the full cart to get updated totals
        await get().fetchCart();
        set({ isUpdating: false });
        return true;
      } else {
        set({
          isUpdating: false,
          error: result.error?.message || 'Failed to update item',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to update cart item:', error);
      set({
        isUpdating: false,
        error: 'Failed to update cart item',
      });
      return false;
    }
  },

  // Remove item from cart
  removeItem: async (itemId: string) => {
    set({ isUpdating: true, error: null });

    try {
      const cartService = getCartService();
      const result = await cartService.removeItem(itemId);

      if (result.success) {
        // Refresh the full cart to get updated totals
        await get().fetchCart();
        set({ isUpdating: false });
        return true;
      } else {
        set({
          isUpdating: false,
          error: result.error?.message || 'Failed to remove item',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
      set({
        isUpdating: false,
        error: 'Failed to remove item from cart',
      });
      return false;
    }
  },

  // Clear entire cart
  clearCart: async () => {
    set({ isUpdating: true, error: null });

    try {
      const cartService = getCartService();
      const result = await cartService.clearCart();

      if (result.success) {
        set({
          cart: emptyCart,
          itemCount: 0,
          isUpdating: false,
        });
        return true;
      } else {
        set({
          isUpdating: false,
          error: result.error?.message || 'Failed to clear cart',
        });
        return false;
      }
    } catch (error) {
      console.error('Failed to clear cart:', error);
      set({
        isUpdating: false,
        error: 'Failed to clear cart',
      });
      return false;
    }
  },

  // Lightweight refresh of just the count (for badge)
  refreshCartCount: async () => {
    try {
      const cartService = getCartService();
      const count = await cartService.getCartCount();
      set({ itemCount: count });
    } catch (error) {
      console.error('Failed to refresh cart count:', error);
    }
  },

  // UI Actions
  openDrawer: () => {
    set({ isDrawerOpen: true });
    // Also fetch latest cart data when opening
    get().fetchCart();
  },

  closeDrawer: () => {
    set({ isDrawerOpen: false });
  },

  toggleDrawer: () => {
    const current = get().isDrawerOpen;
    if (!current) {
      get().openDrawer();
    } else {
      get().closeDrawer();
    }
  },

  // Checkout
  checkout: async (data) => {
    set({ isCheckingOut: true, checkoutError: null });

    try {
      const cartService = getCartService();
      const result = await cartService.checkout(data);

      if (result.success && result.order) {
        set({
          cart: emptyCart,
          itemCount: 0,
          isCheckingOut: false,
          lastOrder: result.order as Order,
        });
        return result;
      } else {
        set({
          isCheckingOut: false,
          checkoutError: result.error?.message || 'Checkout failed',
        });
        return result;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      const errorResponse: CheckoutResponse = {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Checkout failed',
        },
      };
      set({
        isCheckingOut: false,
        checkoutError: errorResponse.error?.message || 'Checkout failed',
      });
      return errorResponse;
    }
  },

  clearCheckoutError: () => {
    set({ checkoutError: null });
  },

  clearLastOrder: () => {
    set({ lastOrder: null });
  },
}));
